// 題目／解說文字：保留換行；Markdown 表格（| a | b | 加上 |:---| 分隔列）畫成真正的表格
import React from 'react';

export type Block = { kind: 'text'; text: string } | { kind: 'table'; head: string[]; rows: string[][] };

const isRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const isSep = (l: string) => /^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/.test(l);
const cells = (l: string) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

export function parseBlocks(text: string): Block[] {
  const lines = text.split('\n');
  const out: Block[] = [];
  let buf: string[] = [];
  const flush = () => { if (buf.length) { out.push({ kind: 'text', text: buf.join('\n') }); buf = []; } };
  for (let i = 0; i < lines.length; i++) {
    if (isRow(lines[i]) && i + 1 < lines.length && isSep(lines[i + 1])) {
      flush();
      const head = cells(lines[i]);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isRow(lines[i])) rows.push(cells(lines[i++]));
      i--;
      out.push({ kind: 'table', head, rows });
    } else buf.push(lines[i]);
  }
  flush();
  // 去掉表格前後多出的空行
  return out.map(b => b.kind === 'text' ? { ...b, text: b.text.replace(/^\n+|\n+$/g, '') } : b).filter(b => b.kind === 'table' || b.text);
}

export const hasTable = (text?: string) => !!text && parseBlocks(text).some(b => b.kind === 'table');

// 朗讀用：表格轉成「標題：值」的句子
export function tableToSpeech(text: string): string {
  return parseBlocks(text).map(b => b.kind === 'text' ? b.text
    : b.rows.map(r => r.map((c, j) => `${b.head[j] ?? ''} ${c}`).join('，')).join('。')).join('。');
}

export default function RichText({ text, style }: { text?: string; style?: React.CSSProperties }) {
  if (!text) return null;
  const blocks = parseBlocks(text);
  const cell: React.CSSProperties = { border: '1px solid var(--border)', padding: '4px 8px', textAlign: 'center', whiteSpace: 'nowrap' };
  return (
    <div style={style}>
      {blocks.map((b, i) => b.kind === 'text'
        ? <div key={i} style={{ whiteSpace: 'pre-line' }}>{b.text}</div>
        : (
          <div key={i} style={{ overflowX: 'auto', margin: '8px 0' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.92em', fontWeight: 400 }}>
              <thead><tr>{b.head.map((h, j) => <th key={j} style={{ ...cell, background: 'var(--bg2)', fontWeight: 700 }}>{h}</th>)}</tr></thead>
              <tbody>{b.rows.map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j} style={cell}>{c}</td>)}</tr>)}</tbody>
            </table>
          </div>
        ))}
    </div>
  );
}
